import { Field } from '@headlessui/react';
import { FC } from 'react';
import { Label } from '@/components/form/label';
import { Select, SelectOption } from '@/components/form/select';
import { TextInput } from '@/components/form/text-input';

export type CountRange = 'all' | '1' | '5' | '10' | '20' | '50';

export type GroupAnalyticsFilters = {
  dateRange: { start: string | undefined; end: string | undefined };
  missedCalls: 'all' | 'missed' | 'answered';
  callEndedBy: 'all' | 'staff' | 'service-user';
  callDuration: 'all' | '5' | '10' | '15' | '30';
  callsMade: CountRange;
  notificationsSent: CountRange;
  announcementsSent: CountRange;
  notificationResponses: CountRange;
};

const missedCallsOptions: SelectOption<string>[] = [
  { label: 'All Calls', value: 'all' },
  { label: 'Missed Only', value: 'missed' },
  { label: 'Answered Only', value: 'answered' },
];

const callEndedByOptions: SelectOption<string>[] = [
  { label: 'All', value: 'all' },
  { label: 'Staff', value: 'staff' },
  { label: 'Service User', value: 'service-user' },
];

const callDurationOptions: SelectOption<string>[] = [
  { label: 'All Durations', value: 'all' },
  { label: 'Up to 5 mins', value: '5' },
  { label: 'Up to 10 mins', value: '10' },
  { label: 'Up to 15 mins', value: '15' },
  { label: 'Up to 30 mins', value: '30' },
];

const countRangeOptions: SelectOption<string>[] = [
  { label: 'Any', value: 'all' },
  { label: '1+', value: '1' },
  { label: '5+', value: '5' },
  { label: '10+', value: '10' },
  { label: '20+', value: '20' },
  { label: '50+', value: '50' },
];

type FilterItemsProps = {
  filters: GroupAnalyticsFilters;
  onFiltersChange: (filters: GroupAnalyticsFilters) => void;
};

export const FilterItems: FC<FilterItemsProps> = ({
  filters,
  onFiltersChange,
}) => {
  const updateFilter = <K extends keyof GroupAnalyticsFilters>(
    key: K,
    value: GroupAnalyticsFilters[K],
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className="grid grid-cols-1 gap-x-1 gap-y-4 md:grid-cols-2 lg:grid-cols-5">
      <Field className="w-2/3">
        <Label className="text-xs">Start Date</Label>
        <TextInput
          className="mt-2 text-xs"
          onChange={(e) =>
            updateFilter('dateRange', {
              ...filters.dateRange,
              start: e.target.value || undefined,
            })
          }
          type="date"
          value={filters.dateRange.start ?? ''}
        />
      </Field>

      <Field className="w-2/3">
        <Label className="text-xs">End Date</Label>
        <TextInput
          className="mt-2 text-xs"
          onChange={(e) =>
            updateFilter('dateRange', {
              ...filters.dateRange,
              end: e.target.value || undefined,
            })
          }
          type="date"
          value={filters.dateRange.end ?? ''}
        />
      </Field>

      <Field className="w-2/3">
        <Label className="text-xs">Missed Calls</Label>
        <Select
          hidePlaceholderOption
          className="mt-2 text-xs"
          onChange={(e) =>
            updateFilter(
              'missedCalls',
              e.target.value as GroupAnalyticsFilters['missedCalls'],
            )
          }
          options={missedCallsOptions}
          value={filters.missedCalls}
        />
      </Field>

      <Field className="w-2/3">
        <Label className="text-xs">Call Ended By</Label>
        <Select
          hidePlaceholderOption
          className="mt-2 text-xs"
          onChange={(e) =>
            updateFilter(
              'callEndedBy',
              e.target.value as GroupAnalyticsFilters['callEndedBy'],
            )
          }
          options={callEndedByOptions}
          value={filters.callEndedBy}
        />
      </Field>

      <Field className="w-2/3">
        <Label className="text-xs">Call Duration</Label>
        <Select
          hidePlaceholderOption
          className="mt-2 text-xs"
          onChange={(e) =>
            updateFilter(
              'callDuration',
              e.target.value as GroupAnalyticsFilters['callDuration'],
            )
          }
          options={callDurationOptions}
          value={filters.callDuration}
        />
      </Field>

      <Field className="w-2/3">
        <Label className="text-xs">Calls Made</Label>
        <Select
          hidePlaceholderOption
          className="mt-2 text-xs"
          onChange={(e) =>
            updateFilter('callsMade', e.target.value as CountRange)
          }
          options={countRangeOptions}
          value={filters.callsMade}
        />
      </Field>

      <Field className="w-2/3">
        <Label className="text-xs">Notifications Sent</Label>
        <Select
          hidePlaceholderOption
          className="mt-2 text-xs"
          onChange={(e) =>
            updateFilter('notificationsSent', e.target.value as CountRange)
          }
          options={countRangeOptions}
          value={filters.notificationsSent}
        />
      </Field>

      <Field className="w-2/3">
        <Label className="text-xs">Announcements Sent</Label>
        <Select
          hidePlaceholderOption
          className="mt-2 text-xs"
          onChange={(e) =>
            updateFilter('announcementsSent', e.target.value as CountRange)
          }
          options={countRangeOptions}
          value={filters.announcementsSent}
        />
      </Field>

      <Field className="w-2/3">
        <Label className="text-xs">Notification Responses</Label>
        <Select
          hidePlaceholderOption
          className="mt-2 text-xs"
          onChange={(e) =>
            updateFilter('notificationResponses', e.target.value as CountRange)
          }
          options={countRangeOptions}
          value={filters.notificationResponses}
        />
      </Field>
    </div>
  );
};
